import React from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Award, FileText, Download, Presentation, GraduationCap } from "lucide-react";
import { fmtDate } from "@/lib/format";

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function CertList({ items, token, emptyText, accent }) {
  if (!items.length) {
    return (
      <div className="p-8 text-center text-sm text-stone-400">{emptyText}</div>
    );
  }
  return (
    <ul className="divide-y divide-stone-100">
      {items.map((c) => (
        <li key={c.id} data-testid={`portal-cert-${c.id}`}>
          <a
            href={`${API_BASE}/students/certificates/${c.id}/download?auth=${encodeURIComponent(token || "")}`}
            target="_blank"
            rel="noreferrer"
            className="p-4 flex items-center gap-3 hover:bg-stone-50/60 transition-colors"
          >
            <div
              className="w-10 h-10 rounded-lg grid place-items-center shrink-0"
              style={{ background: `${accent}15`, color: accent }}
            >
              <Award size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[#1C352D] truncate">{c.title}</div>
              <div className="text-[11px] text-stone-500 truncate mt-0.5">
                {c.original_filename} · {formatSize(c.size)} · rilasciato {fmtDate(c.uploaded_at)}
              </div>
            </div>
            <Download size={16} className="text-stone-400 shrink-0" />
          </a>
        </li>
      ))}
    </ul>
  );
}

export default function PortalAttestati() {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const { data: certs = [], isLoading } = useQuery({
    queryKey: ["portal-my-certificates"],
    queryFn: async () => (await api.get("/students/me/certificates")).data,
  });

  const seminars = certs.filter((c) => c.kind === "seminario");
  const courses = certs.filter((c) => c.kind === "corso");

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto" data-testid="portal-attestati">
      <PageHeader
        title="I miei attestati"
        subtitle="Consulta e scarica i tuoi attestati di seminari e corsi rilasciati dalla scuola."
      />

      {isLoading ? (
        <div className="text-sm text-stone-400 text-center py-8">Caricamento…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
            <div className="p-4 border-b border-stone-100 bg-[#FDFBF7] flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white grid place-items-center border border-stone-200 text-[#0369A1]">
                <Presentation size={16} />
              </div>
              <div>
                <div className="font-medium text-[#1C352D]">Seminari</div>
                <div className="text-[11px] text-stone-500">
                  {seminars.length} {seminars.length === 1 ? "attestato" : "attestati"}
                </div>
              </div>
            </div>
            <CertList items={seminars} token={token} emptyText="Nessun attestato di seminario disponibile." accent="#0369A1" />
          </Card>

          <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
            <div className="p-4 border-b border-stone-100 bg-[#FDFBF7] flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white grid place-items-center border border-stone-200 text-[#15803D]">
                <GraduationCap size={16} />
              </div>
              <div>
                <div className="font-medium text-[#1C352D]">Corsi</div>
                <div className="text-[11px] text-stone-500">
                  {courses.length} {courses.length === 1 ? "attestato" : "attestati"}
                </div>
              </div>
            </div>
            <CertList items={courses} token={token} emptyText="Nessun attestato di corso disponibile." accent="#15803D" />
          </Card>
        </div>
      )}
    </div>
  );
}
